/*
 * ew-fitment-filter.js
 *
 * Site-wide fitment filtering. When an active vehicle is set
 * (localStorage.ew_active_vehicle.code), hide product cards whose
 * tag set doesn't include the chassis tag (chassis-{code, lowercased}).
 *
 * Hooks (no Liquid changes needed in product-card snippets — we read the
 * attribute we render on each card body container):
 *   - <a class="ew-product" data-product-tags="...comma list..."> — EW cards
 *   - any [data-product-tags] element generally
 *
 * For surfaces without the data-product-tags attribute we bail (graceful).
 *
 * UI: appends a small "Filtering for {label} ·  Show all  ·  Clear" bar
 * at the top of any product grid on collection / search pages.
 *
 * Vanilla JS, no deps.
 */
(function () {
  'use strict';

  var LS_KEY = 'ew_active_vehicle';
  var SHOW_ALL_KEY = 'ew_fitment_show_all';
  var BAR_SUPPRESSED = true; // FITMENT-BAR-1 launch suppression — flip to false to restore the fitment bar

  function readActive() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch (_) { return null; }
  }
  function readShowAll() {
    return sessionStorage.getItem(SHOW_ALL_KEY) === '1';
  }
  function setShowAll(v) {
    if (v) sessionStorage.setItem(SHOW_ALL_KEY, '1');
    else sessionStorage.removeItem(SHOW_ALL_KEY);
  }

  /* Decide if we're on a surface where filtering applies. */
  function isFilterableSurface() {
    var p = window.location.pathname;
    /* BUG-VFILTER-1 — dedicated picker / nav pages carry their own vehicle UI
       (shop-by-vehicle's chassis picker; shop-by-brand / shop-by-category
       landings). The hide-in-place fitment bar double-fired there, so exclude
       the whole /pages/shop-by-* family BEFORE the /pages/shop check below. */
    if (/^\/pages\/shop-by-/.test(p)) return false;
    /* Collection pages, search, /pages/shop, related-products */
    if (/^\/collections\//.test(p)) return true;
    if (/^\/search/.test(p)) return true;
    if (p === '/pages/shop' || /^\/pages\/shop\b/.test(p)) return true;
    /* PDPs: only the related-products module within them */
    if (/^\/products\//.test(p)) return 'related-only';
    return false;
  }

  function cardScopes(scopeMode) {
    if (scopeMode === 'related-only') {
      var related = document.querySelector('.related-products, [data-related-products]');
      return related ? [related] : [];
    }
    return [document.body];
  }

  function tagsForCard(card) {
    var raw = card.getAttribute('data-product-tags') || '';
    return raw.split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
  }

  /* Two fitment vocabularies in the catalog:
       chassis-g20  (hyphen)  — hardware (downpipes, intercoolers, …)
       chassis:G20  (colon)   — tunes
       engine:B58             — bundles/kits + every fitment-tagged product
     Hardware carries both chassis-* AND engine:*; tunes carry chassis:* +
     engine:*; bundles carry ONLY engine:* (no chassis tag, so they were
     escaping the chassis filter entirely — every kit stayed visible
     regardless of the selected vehicle). Engine-aware fallback below
     fixes that without mass-adding chassis tags to bundles. */
  function chassisCodesFor(tags) {
    var out = [];
    for (var i = 0; i < tags.length; i++) {
      var t = tags[i];
      if (t.indexOf('chassis-') === 0 || t.indexOf('chassis:') === 0) {
        out.push(t.substring(8));
      }
    }
    return out;
  }
  function engineCodesFor(tags) {
    var out = [];
    for (var i = 0; i < tags.length; i++) {
      var t = tags[i];
      if (t.indexOf('engine:') === 0 || t.indexOf('engine-') === 0) {
        out.push(t.substring(7));
      }
    }
    return out;
  }

  /* Returns true (fits), false (doesn't fit), or null (universal — keep
     visible regardless of vehicle). Chassis is authoritative when the
     product carries chassis tags; engine is the fallback for engine-level
     products (bundles, engine-only hardware). */
  /* The acceptable engine keys for the active vehicle: its stored code AND its family.
   *
   * FIXED 2026-08-10 (WO-2B-v3 Phase 0C). This used to compare the stored `engine_code` to the tag
   * list with a single exact `indexOf`. But ew_active_vehicle.engine_code is stored RAW by design —
   * assets/ew-vehicle-resolver.js: "NEVER mutate the stored engine_code used for matching/tune
   * resolution — only shorten the label" — so a vehicle carrying a full BMW code (S63B44T4,
   * B48B20O1, S58B30T0 — already 45 of 267 rows in ew-chassis-data.json) matched NO tag, and every
   * engine-tagged / chassis-untagged product (bundles and kits, per the note above) was silently
   * hidden with the count label rewritten to agree.
   *
   * ⛔ Normalizing the vehicle side ALONE is wrong and was the tempting fix. The tag vocabulary is
   * NOT purely family-form — it contains engine:N63TU, engine:N63TU2, engine:N63TU3 and engine:S63TU
   * alongside engine:N63/engine:S63, plus engine:B46 and engine:N26, which are exactly the two codes
   * ENGINE_ALIAS folds away (B46->B48, N26->N20). Collapsing the vehicle to its family would have
   * stopped a B46 MINI matching engine:B46 and an N63TU3 car matching engine:N63TU3 — breaking
   * matches that work today.
   *
   * So accept BOTH. This is a pure WIDENING: it can only un-hide a product, never newly hide one,
   * which is the right risk posture for a filter whose failure mode is silent disappearance. It is
   * also stable across WO-2B Phase 0A, which turns ~96 more chassis engine_codes into full codes.
   *
   * engineFamily() is the one canonical shortener and this file deliberately keeps NO private copy
   * (CLAUDE.md NO DUPLICATION — there are already ~10). If the resolver has not loaded, the raw key
   * still applies and behaviour is exactly what it is today. */
  function engineKeysFor(raw) {
    var s = String(raw || '').trim();
    if (!s) return [];
    var keys = [s.toLowerCase()];
    var R = window.EWVehicleResolver;
    if (R && typeof R.engineFamily === 'function') {
      var fam = String(R.engineFamily(s) || '').toLowerCase();
      if (fam && keys.indexOf(fam) === -1) keys.push(fam);
    }
    return keys;
  }

  function cardMatches(card, chassisCode, engineKeys) {
    var tags = tagsForCard(card);
    if (!tags.length) return null;
    var chassis = chassisCodesFor(tags);
    var engine  = engineCodesFor(tags);
    if (!chassis.length && !engine.length) return null;   /* universal */
    if (chassis.length) return chassis.indexOf(chassisCode) !== -1;
    if (!engineKeys.length) return null;                   /* no engine known → can't decide */
    for (var i = 0; i < engine.length; i++) {
      if (engineKeys.indexOf(engine[i]) !== -1) return true;
    }
    return false;
  }

  /* When a vehicle filter is active on a server-paginated collection, the
     visible page only contains its server-side 24 raw products. Hiding
     non-matches in place leaves sparse pages with the matches scattered
     across the paginator. The fix: pull the rest of the collection's
     pages once via fetch(), append every card into the current page's
     grid, and hide the server paginator. The downstream display:none
     filter (CSS Grid auto-repacks) then renders a contiguous grid of
     matches — no gaps, no scatter. We only do this when a vehicle is
     active, so no perf hit for no-vehicle browsing. */
  /* The full-collection load must run EXACTLY once. applyFilter() fires from
     two near-simultaneous triggers on load (the IIFE-bottom call and the
     ew:vehicle-resolved listener). A boolean flag set inside the async
     .then() is still false on the second synchronous re-entry, so both
     callers ran the fetch+append → page-2 cards appended twice. Caching the
     in-flight promise makes the second caller await the same work instead of
     starting its own. */
  var loadPromise = null;

  function findPaginatedGrid() {
    var nav = document.querySelector('nav[aria-label="Pagination"]');
    if (!nav) return null;
    /* The collection grid that the paginator paginates over. The shared
       collection section marks the PRODUCT grid with [data-ew-product-grid]
       so we never target the bridge/promo .ew-grid-4 (which is the first
       .ew-grid-4 in the section on esse-werks). Fall back to the old
       selectors for other surfaces that have no bridge grid. */
    var grid = document.querySelector('section.ew-bay [data-ew-product-grid]')
            || document.querySelector('section.ew-bay .ew-grid-4')
            || document.querySelector('.ew-grid-4');
    if (!grid) return null;
    var info = nav.querySelector('.ew-caption');
    var totalPages = 1;
    if (info) {
      var m = info.textContent.match(/Page \s*\d+\s*of\s*(\d+)/i);
      if (m) totalPages = parseInt(m[1], 10);
    }
    if (totalPages <= 1) return null;
    var currentPage = 1;
    try {
      var pp = new URLSearchParams(window.location.search).get('page');
      if (pp) currentPage = parseInt(pp, 10) || 1;
    } catch (_) {}
    return { grid: grid, nav: nav, totalPages: totalPages, currentPage: currentPage };
  }

  function ensureCollectionFullyLoaded() {
    /* Cached-promise guard: synchronous on re-entry, so concurrent callers
       await the SAME load instead of each kicking off their own fetch+append. */
    if (loadPromise) return loadPromise;
    var info = findPaginatedGrid();
    if (!info) { loadPromise = Promise.resolve(); return loadPromise; }
    var grid = info.grid, nav = info.nav, totalPages = info.totalPages, currentPage = info.currentPage;
    var promises = [];
    for (var p = 1; p <= totalPages; p++) {
      if (p === currentPage) continue;
      var u = new URL(window.location.href);
      u.searchParams.set('page', String(p));
      u.searchParams.delete('_cb'); u.searchParams.delete('_');
      var url = u.pathname + u.search;
      promises.push(fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return [];
          try {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var srcGrid = doc.querySelector('[data-ew-product-grid]') || doc.querySelector('section.ew-bay .ew-grid-4') || doc.querySelector('.ew-grid-4');
            return srcGrid ? Array.from(srcGrid.children) : [];
          } catch (_) { return []; }
        })
        .catch(function () { return []; }));
    }
    loadPromise = Promise.all(promises).then(function (batches) {
      batches.forEach(function (arr) {
        arr.forEach(function (node) {
          /* appendChild auto-adopts from the parsed Document. */
          grid.appendChild(node);
        });
      });
      nav.style.display = 'none';
      nav.setAttribute('data-ew-fitment-paginator-hidden', 'true');
    }).catch(function () {});
    return loadPromise;
  }

  function applyFilter() {
    var surface = isFilterableSurface();
    if (!surface) return;
    var active = readActive();
    if (!active || !(active.code || active.chassis_code || active.chassis)) {
      teardownBar();
      restoreAll();
      restoreCountLabel();
      return;
    }
    /* Vehicle active on a multi-page collection → gather the rest before
       running the per-card filter, so the result is contiguous. */
    ensureCollectionFullyLoaded().then(applyFilterToVisibleCards);
  }

  function applyFilterToVisibleCards() {
    var surface = isFilterableSurface();
    if (!surface) return;
    var active = readActive();
    if (!active || !(active.code || active.chassis_code || active.chassis)) return;
    /* Migrate-on-read: canonical `code`, falling back to the legacy
       chassis_code / chassis field names a previously-persisted
       ew_active_vehicle may carry. EWVehicleResolver.write now writes
       `code` (+ legacy aliases), but read defensively for stale values. */
    var chassisCode = String(active.code || active.chassis_code || active.chassis || '').toLowerCase();
    var engineKeys  = engineKeysFor(active.engine_code || active.engine);
    var showAll = readShowAll();
    var scopes = cardScopes(surface);
    var hidden = 0, unknown = 0, total = 0, fits = 0;

    scopes.forEach(function (scope) {
      scope.querySelectorAll('[data-product-tags]').forEach(function (card) {
        total++;
        var m = cardMatches(card, chassisCode, engineKeys);
        if (m === null) { unknown++; card.removeAttribute('data-ew-fitment-hidden'); card.style.display = ''; return; }
        if (m) fits++;
        if (m || showAll) {
          card.removeAttribute('data-ew-fitment-hidden');
          card.style.display = '';
        } else {
          card.setAttribute('data-ew-fitment-hidden', 'true');
          card.style.display = 'none';
          hidden++;
        }
      });
    });

    /* Cards visible right now = fits + unknown (untagged/universal) when narrowed,
       OR everything (total) when showAll. The header count must match. */
    var shown = showAll ? total : (fits + unknown);
    updateCountLabel(shown, total, showAll, hidden);
    renderBar(active, fits, unknown, total, hidden, showAll);
  }

  function restoreAll() {
    document.querySelectorAll('[data-ew-fitment-hidden]').forEach(function (el) {
      el.removeAttribute('data-ew-fitment-hidden');
      el.style.display = '';
    });
  }

  /* Collection-page count label: keep cards-shown ↔ count in sync. The
     original Liquid renders "{{ collection.products_count }} products"
     into [data-collection-count]; we cache that initial text and overwrite
     it when the fitment filter is narrowing. */
  function updateCountLabel(shown, total, showAll, hidden) {
    var el = document.querySelector('[data-collection-count]');
    if (!el) return;
    if (el.getAttribute('data-ew-count-original') == null) {
      el.setAttribute('data-ew-count-original', el.textContent.trim());
    }
    if (showAll || hidden === 0) {
      el.textContent = total + ' ' + (total === 1 ? 'product' : 'products');
    } else {
      el.textContent = shown + ' of ' + total + ' ' + (total === 1 ? 'product' : 'products') + ' fit';
    }
  }
  function restoreCountLabel() {
    var el = document.querySelector('[data-collection-count]');
    if (!el) return;
    var orig = el.getAttribute('data-ew-count-original');
    if (orig != null) el.textContent = orig;
  }

  /* Fitment status bar (sticky-ish, top of main content). When the filter
     is narrowing, the label spells out "Showing X of N that fit your
     <vehicle> — Show all N" so the bar and the in-page count always agree. */
  function renderBar(vehicle, fits, unknown, total, hidden, showAll) {
    if (BAR_SUPPRESSED) { teardownBar(); return; }
    var bar = document.querySelector('[data-ew-fitment-bar]');
    if (!bar) {
      bar = document.createElement('div');
      bar.setAttribute('data-ew-fitment-bar', '');
      bar.className = 'ew-fitment-bar';
      bar.setAttribute('role', 'region'); bar.setAttribute('aria-label', 'Vehicle fitment filter');
      bar.innerHTML = [
        '<div class="ew-fitment-bar__inner">',
        '  <span class="ew-fitment-bar__label" data-ew-fitment-label role="status" aria-live="polite"></span>',
        '  <div class="ew-fitment-bar__actions">',
        '    <button type="button" data-ew-fitment-toggle></button>',
        '    <button type="button" data-ew-fitment-clear>Clear vehicle</button>',
        '    <a href="/pages/contact">Not sure? Talk to us →</a>',
        '  </div>',
        '</div>',
      ].join('');
      var insertBefore = document.querySelector('main') || document.querySelector('.shopify-section') || document.body.firstElementChild;
      if (insertBefore && insertBefore.parentNode) {
        insertBefore.parentNode.insertBefore(bar, insertBefore);
      } else {
        document.body.insertBefore(bar, document.body.firstChild);
      }
      injectStyle();
    }
    /* F-05 — canonical formatter (year only with provenance; honest ys–ye
       band otherwise). Never vehicle.label first: a LEGACY persisted label
       carries the old fabricated band-end year. */
    var vehLabel = '';
    try {
      if (window.EWVehicleResolver && typeof window.EWVehicleResolver.displayLabel === 'function') {
        vehLabel = window.EWVehicleResolver.displayLabel(vehicle);
      }
    } catch (_) {}
    vehLabel = vehLabel || vehicle.code || vehicle.chassis_code || 'vehicle';
    var labelEl  = bar.querySelector('[data-ew-fitment-label]');
    var shown    = showAll ? total : (fits + unknown);

    if (showAll) {
      labelEl.textContent = 'Showing all ' + total + ' — vehicle filter off for ' + vehLabel;
    } else if (hidden === 0) {
      labelEl.textContent = 'All ' + total + ' fit your ' + vehLabel;
    } else {
      labelEl.textContent = 'Showing ' + shown + ' of ' + total + ' that fit your ' + vehLabel;
    }

    var tog = bar.querySelector('[data-ew-fitment-toggle]');
    tog.textContent = showAll ? ('Show only what fits') : ('Show all ' + total);
    tog.onclick = function () { setShowAll(!showAll); applyFilter(); };
    /* Hide toggle when there's nothing to toggle (no hidden products either way). */
    tog.style.display = (hidden === 0 && !showAll) ? 'none' : '';
    bar.querySelector('[data-ew-fitment-clear]').onclick = function () {
      if (window.EWVehicleResolver) window.EWVehicleResolver.clear();
    };
  }

  function teardownBar() {
    var bar = document.querySelector('[data-ew-fitment-bar]');
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
  }

  function injectStyle() {
    if (document.getElementById('ew-fitment-bar-style')) return;
    var s = document.createElement('style'); s.id = 'ew-fitment-bar-style';
    s.textContent = [
      '.ew-fitment-bar{background:var(--ew-graphite);border-bottom:1px solid var(--accent);padding:10px 0;font-size:12px;color:var(--ew-paper);position:sticky;top:var(--ew-nav-h, 72px);z-index:50;}',
      '.ew-fitment-bar__inner{max-width:1320px;margin:0 auto;padding:0 24px;display:flex;align-items:center;flex-wrap:wrap;gap:14px;}',
      '.ew-fitment-bar__label{color:var(--ew-white);text-transform:uppercase;letter-spacing:0.10em;font-size:11px;}',
      '.ew-fitment-bar__hidden{color:var(--ew-aluminum);font-size:11px;}',
      '.ew-fitment-bar__actions{margin-left:auto;display:flex;gap:14px;align-items:center;}',
      '.ew-fitment-bar__actions button{background:transparent;border:none;color:var(--ew-paper);font:inherit;font-size:11px;text-transform:uppercase;letter-spacing:0.10em;cursor:pointer;padding:6px 4px;border-bottom:1px solid var(--ew-aluminum);min-height:24px;line-height:1;display:inline-flex;align-items:center;}',
      '.ew-fitment-bar__actions button:hover,.ew-fitment-bar__actions a:hover{color:var(--ew-white);border-color:var(--accent);}',
      '.ew-fitment-bar__actions a{color:var(--ew-paper);text-decoration:none;font-size:11px;text-transform:uppercase;letter-spacing:0.10em;padding:6px 4px;border-bottom:1px solid var(--ew-aluminum);min-height:24px;display:inline-flex;align-items:center;}',
    ].join('\n');
    document.head.appendChild(s);
  }

  /* Re-filter when vehicle changes, on initial load. */
  document.addEventListener('ew:vehicle-resolved', applyFilter);
  document.addEventListener('ew:vehicle-cleared', function () { setShowAll(false); applyFilter(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyFilter);
  else applyFilter();

  window.EWFitmentFilter = { apply: applyFilter, restore: restoreAll };
})();
